import React from "react";
import {
  Box,
  Button,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from "@mui/material";
import { Plus } from "@phosphor-icons/react";
import { theme } from "@answersnow/pkg-theme";

const HEADER_CELL_SX = {
  fontWeight: 500,
  fontSize: "14px",
  lineHeight: "24px",
  letterSpacing: "0.17px",
  color: "text.primary",
  p: 4,
};

const BODY_CELL_SX = {
  fontSize: "14px",
  lineHeight: "1.43",
  letterSpacing: "0.17px",
  color: "text.primary",
  p: 4,
};

export interface DataTableColumn {
  key: string;
  label: string;
  width?: string | number;
}

export interface DataTableRow {
  id: string;
  [key: string]: unknown;
}

interface DataTableProps {
  columns: DataTableColumn[];
  rows: DataTableRow[];
  emptyMessage?: string;
  ariaLabel?: string;
  onAddNew?: () => void;
  sx?: SxProps<Theme>;
  renderCell?: (col: DataTableColumn, row: DataTableRow) => React.ReactNode;
}

export const DataTable: React.FC<DataTableProps> = ({
  columns,
  rows,
  emptyMessage = "No records found",
  ariaLabel = "Data table",
  onAddNew,
  sx,
  renderCell,
}) => {
  return (
    <Box sx={sx}>
      <Box
        sx={{
          borderRadius: "16px",
          border: "1px solid",
          borderColor: theme.palette.divider,
          overflow: "hidden",
          overflowX: { xs: "auto", md: "visible" },
        }}
      >
        <Table aria-label={ariaLabel} sx={{ borderCollapse: "collapse" }}>
          <TableHead>
            <TableRow sx={{ borderBottom: "1px solid", borderColor: theme.palette.divider }}>
              {columns.map((col) => (
                <TableCell key={col.key} sx={{ ...HEADER_CELL_SX, ...(col.width ? { width: col.width, whiteSpace: "nowrap" } : {}) }}>
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ p: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <TableRow
                  key={row.id}
                  sx={{
                    borderBottom:
                      index < rows.length - 1 ? "1px solid" : "0px solid transparent",
                    borderColor: theme.palette.divider,
                    ...(index === rows.length - 1 && {
                      "& .MuiTableCell-root": { borderBottom: "none" },
                    }),
                  }}
                >
                  {columns.map((col) => (
                    <TableCell key={col.key} sx={{ ...BODY_CELL_SX, ...(col.width ? { width: col.width, whiteSpace: "nowrap" } : {}) }}>
                      {renderCell ? renderCell(col, row) : ((row[col.key] as string) || "-")}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>

      {onAddNew && (
        <Button
          variant="contained"
          size="small"
          color="secondary"
          startIcon={<Plus />}
          onClick={(event) => {
            event.currentTarget.blur();
            onAddNew();
          }}
          sx={{ mt: 7, width: "fit-content" }}
        >
          Add New
        </Button>
      )}
    </Box>
  );
};

export default DataTable;
